import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { api, setCsrfToken } from "@/lib/api";

export default function Login() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Public pre-auth listing — id/name/type only. Empty when nx.sso is off.
  const sso = useQuery({
    queryKey: ["sso-providers"],
    queryFn: () => api.get<{ providers: { id: string; name: string; type: string }[] }>(
      "/api/sso/providers").catch(() => ({ providers: [] })),
    staleTime: 60_000,
    retry: false,
  });

  async function finishLogin() {
    const csrf = await api.get<{ csrf_token: string }>("/api/auth/csrf");
    setCsrfToken(csrf.csrf_token);
    await qc.invalidateQueries({ queryKey: ["me"] });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!challenge) {
        const r = await api.post<{ mfa_required?: boolean; challenge?: string }>(
          "/api/auth/login",
          { email, password },
        );
        if (r.mfa_required && r.challenge) {
          setChallenge(r.challenge);
          return;
        }
      } else {
        await api.post("/api/auth/totp/verify", { challenge, code });
      }
      await finishLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : "login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden">
      <div className="bg-grid absolute inset-0" />
      <Card className="relative w-full max-w-sm border-border/80 shadow-2xl shadow-black/40">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 ring-1 ring-primary/30">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-xl">NXShield</CardTitle>
          <CardDescription>Application security console — BunkerWeb 1.6.15</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {!challenge ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" required
                    value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
              </>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="code">Authenticator or recovery code</Label>
                <Input id="code" required autoFocus placeholder="123456 or RECOVERYCODE"
                  value={code} onChange={(e) => setCode(e.target.value)} />
                <p className="text-xs text-muted-foreground">
                  Second factor required for this account.
                </p>
              </div>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button className="w-full" disabled={busy}>
              {busy ? "…" : challenge ? "Verify" : "Sign in"}
            </Button>
          </form>
          {(sso.data?.providers.length ?? 0) > 0 && (
            <div className="mt-4 space-y-2 border-t pt-4">
              <p className="text-center text-xs text-muted-foreground">
                or continue with
              </p>
              {sso.data!.providers.map((p) => (
                <a key={p.id}
                  href={`/api/sso/login/${p.id}?next=${encodeURIComponent("/")}`}
                  className="block rounded-md border border-border px-3 py-2 text-center text-sm font-medium hover:bg-accent">
                  {p.name} ({p.type.toUpperCase()})
                </a>
              ))}
              <p className="text-center text-[10px] text-muted-foreground">
                local owner sign-in always remains available
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
